import React from 'react';
import { bool, func, object, string } from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { intlShape } from 'react-intl';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';
import { path, pathOr } from 'ramda';

import config from '../../../../config';
import {
  convertMoneyToNumber,
  getCurrencySymbol,
} from '../../../../util/currency';
import * as validators from '../../../../util/validators';
import { stringValuesToNumbers } from '../../../../util/data';
import {
  Form,
  FieldSelect,
  PrimaryButton,
} from '../../../../components';
import { isNilOrEmpty } from '../../../../util/validators';
import { applyVariation } from '../../EditListingPricingPanel.Helpers';
import css from './HoursDiscountForm.module.css';

const MIN_VARIATION = 5;
const MAX_VARIATION = 50;

const HoursDiscountForm = (props) => {
  const {
    className,
    intl,
    isMobile,
    listing,
    onSubmit,
    updated,
    updateInProgress,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;
  const handleDeactivate = () => {
    const publicData = { pricingExtraHours: undefined };

    onSubmit({ publicData });
    history.push(`${pathname}`);
  };
  const handleSubmit = (values) => {
    const {
      extraHoursDiscountEnabled,
    } = values;
    const publicData = extraHoursDiscountEnabled !== 'oui' ? {
      pricingExtraHours: null,
    } : {
      pricingExtraHours: {
        vartiationType: 'reduction',
        variation: 50,
        fromNbHours: 3,
      },
    };
    const updatedValues = { publicData };
    onSubmit(updatedValues);
  };
  const listingPublicData = pathOr({}, ['attributes', 'publicData'], listing);
  const listingPrice = pathOr(0, ['attributes', 'price'], listing);
  const currencySymbol = getCurrencySymbol(config.currencyConfig);
  const { pricingExtraHours } = listingPublicData;
  const currentVariation = path(['variation'], pricingExtraHours) || MIN_VARIATION;
  const initialValues = {
    extraHoursDiscountEnabled: !isNilOrEmpty(pricingExtraHours) ? 'oui' : 'non',
  };
  const maxVariationErrorText = intl.formatMessage(
    { id: 'EditListingPricingPanel.HoursDiscountForm.maxVariationError' },
    { percentage: `${MAX_VARIATION}%` },
  );
  const minVariationErrorText = intl.formatMessage(
    { id: 'EditListingPricingPanel.HoursDiscountForm.minVariationError' },
    { percentage: `${MIN_VARIATION}%` },
  );
  const maxValidator = validators.maxNumberAsString(MAX_VARIATION, maxVariationErrorText);
  const minValidator = validators.minNumberAsString(MIN_VARIATION, minVariationErrorText);
  const validate = validators.composeValidators(maxValidator, minValidator);
  const labelText = intl.formatMessage({ id : 'EditListingPricingPanel.HoursDiscountForm.variationLabel' });
  const labelDesktopText = intl.formatMessage({ id : 'EditListingPricingPanel.HoursDiscountForm.variationLabelDesktop' });
  const deactivateText = intl.formatMessage({ id: 'Marketplace.deactivate' });
  const submitText = intl.formatMessage({ id: 'Marketplace.save' });
  const yes = intl.formatMessage({ id: 'Marketplace.yes' });
  const no = intl.formatMessage({ id: 'Marketplace.no' });
  const selectPlaceholder = intl.formatMessage({ id: 'EditListingInformationForm.selectPlaceholder' });
  const reductionExtraHourLabel = intl.formatMessage({ id: 'EditListingForm.reductionExtraHourLabel' });
  const requiredMessage = intl.formatMessage({ id: 'Marketplace.requiredMessage' });
  const classes = classNames(css.root, className);

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      render={formRenderProps => {
        const {
          handleSubmit,
          invalid,
          pristine,
          values,
        } = formRenderProps;
        const submitReady = (updated && pristine);
        const submitDisabled = invalid || updateInProgress;

        return (
          <Form className={classes} id="weekendPricingForm" onSubmit={handleSubmit}>
            <FieldSelect
              className={css.featuresItem}
              id="extraHoursDiscountEnabled"
              name="extraHoursDiscountEnabled"
              label={reductionExtraHourLabel}
              validate={validators.required(requiredMessage)}
            >
              <option value="">{selectPlaceholder}</option>
              <option value="oui">{yes}</option>
              <option value="non">{no}</option>
            </FieldSelect>
            <div className={css.buttonContainer}>
              <button className={css.deactivateButton} onClick={handleDeactivate} type="button">
                <span className={css.deactivateText}>{deactivateText}</span>
              </button>
              <PrimaryButton
                className={css.submitButton}
                type="submit"
                form="weekendPricingForm"
                inProgress={updateInProgress}
                disabled={submitDisabled}
                ready={submitReady}
              >
                <span>{submitText}</span>
              </PrimaryButton>
            </div>
          </Form>
        )
      }}
    />
  )
}

HoursDiscountForm.defaultProps = {
  className: null,
  listing: null,
};

HoursDiscountForm.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  isMobile: bool.isRequired,
  listing: object,
  onSubmit: func.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
};

export default HoursDiscountForm;
